import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListRegionsQuery } from '../../../../../../graphql/fragment/audiences/ListRegions.generated';
import { US_REGION_CODE } from '../../../constants';
import type { UseMarketCountriesReturn } from './useMarketCountries';

type UseMarketDisplayProps = {
  selectedRegionCode: string | null;
  isRegionSelected: boolean;
  selectedCountries: string[];
  pendingRegionCode: string | null;
  regions: ListRegionsQuery['region'];
  countries: UseMarketCountriesReturn['countries'];
};

export type UseMarketDisplayReturn = {
  countryInputValue: string;
  setCountryInputValue: (value: string) => void;
  availableCountries: UseMarketCountriesReturn['countries'];
  shouldFetchCountries: boolean;
  selectedCount: number;
  regionLabel: string | null;
  confirmationItem: string;
  pendingRegionLabel: string | null;
};

export const useMarketDisplay = ({
  selectedRegionCode,
  isRegionSelected,
  selectedCountries,
  pendingRegionCode,
  regions,
  countries,
}: UseMarketDisplayProps): UseMarketDisplayReturn => {
  const { t } = useTranslation();

  const [countryInputValue, setCountryInputValue] = useState('');

  const availableCountries = countries.filter(
    country =>
      !selectedCountries.includes(country.code) && country.name.toLowerCase().includes(countryInputValue.toLowerCase())
  );

  const shouldFetchCountries = selectedRegionCode != null && selectedRegionCode !== US_REGION_CODE;

  const selectedCount = (isRegionSelected ? 1 : 0) + (selectedCountries.length > 0 ? selectedCountries.length : 0);

  const regionLabel =
    selectedRegionCode != null
      ? (regions.find(region => region.code === selectedRegionCode)?.name ?? selectedRegionCode)
      : null;

  const confirmationItem =
    regionLabel != null && selectedRegionCode != null ? `${regionLabel} (${selectedRegionCode})` : t('Region');

  const pendingRegionLabel =
    pendingRegionCode != null
      ? (regions.find(region => region.code === pendingRegionCode)?.name ?? pendingRegionCode)
      : null;

  return {
    countryInputValue,
    setCountryInputValue,
    availableCountries,
    shouldFetchCountries,
    selectedCount,
    regionLabel,
    confirmationItem,
    pendingRegionLabel,
  };
};
